import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { Card, Form, Button, Spinner, Row, Col, InputGroup } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { toast } from "react-toastify";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import MentionEditor from "components/common/MentionEditor";
import { useUserManagement } from "hooks";
import useDebounce from "hooks/route-rules/useDebounce";
import paths from "routes/paths";
import useTasks from "hooks/projects/useTasks";
import { useAuth } from "contexts/AuthContext";
import { useFileUpload } from "hooks/useFileUpload";
import { processDescriptionImages } from "utils/taskUtils";
import { getTaskAssigneeInfo } from "utils/taskAssignee";

const dedupeTextFilesByFileId = (files = []) => {
  if (!Array.isArray(files) || files.length === 0) return [];

  const byFileId = new Map();
  files.forEach((file) => {
    const fileId = file?.fileId;
    if (!fileId) return;
    if (!byFileId.has(fileId)) {
      byFileId.set(fileId, {
        fileId,
        width: Number(file?.width) > 0 ? Number(file.width) : 1,
        height: Number(file?.height) > 0 ? Number(file.height) : 1,
      });
    }
  });

  return [...byFileId.values()];
};

const extractReferencedFileIds = (...htmlBlocks) => {
  const ids = new Set();
  const fileServicePattern = /file-service:\/\/([0-9a-fA-F-]{36})/g;
  const arrayRefPattern = /\[([0-9a-fA-F-]{36})\s*,\s*\d+\s*,\s*\d+\]/g;

  htmlBlocks.forEach((html) => {
    if (!html) return;
    for (const match of html.matchAll(fileServicePattern)) {
      if (match[1]) ids.add(match[1]);
    }
    for (const match of html.matchAll(arrayRefPattern)) {
      if (match[1]) ids.add(match[1]);
    }
  });

  return ids;
};

const TaskFormPage = () => {
  const navigate = useNavigate();
  const { projectId, iterationId, taskId } = useParams();
  const { tasksByIteration, createTask, updateTask, fetchTasksByIteration } = useTasks(projectId, iterationId);
  const tasks = tasksByIteration[iterationId] || [];
  const { users, searchUserProfiles, getUserById } = useUserManagement(
    0,
    20,
    null,
    false,
  );
  const { user } = useAuth();
  const { uploadFile } = useFileUpload();
  const [isLoading, setIsLoading] = useState(!!taskId);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasLoadedInitialData, setHasLoadedInitialData] = useState(false);
  const [userSearchTerm, setUserSearchTerm] = useState("");
  const debouncedUserSearch = useDebounce(userSearchTerm, 300);
  const [searchedUsers, setSearchedUsers] = useState([]);
  const [isSearchingUsers, setIsSearchingUsers] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    acceptanceCriteria: "",
    status: "OPEN",
    priority: 2,
    points: 1,
    assignedUsers: [],
    startedAt: "",
    expectedCompletionDate: "",
    links: [],
    textFiles: [], // Track inline images
  });
  const inlineUploads = useRef({ pending: new Map(), inFlight: 0 });
  const initialTextFileIdsRef = useRef(new Set());

  const isEditMode = !!taskId;

  const priorityOptions = [
    { value: 0, label: "Highest Priority (0)" },
    { value: 1, label: "High Priority (1)" },
    { value: 2, label: "Medium Priority (2)" },
    { value: 3, label: "Low Priority (3)" },
    { value: 4, label: "Lowest Priority (4)" },
  ];

  const pointsOptions = [1, 2, 3, 5, 8];
  const mentionUsers = useMemo(
    () =>
      (Array.isArray(users) ? users : [])
        .filter((u) => u && u.id)
        .map((u) => ({
          id: String(u.id),
          name:
            `${u.firstName || ""} ${u.lastName || ""}`.trim() ||
            u.name ||
            u.email ||
            String(u.id),
          email: u.email || "",
        })),
    [users],
  );

  // Load task data if in edit mode (only once)
  useEffect(() => {
    const loadTaskData = async () => {
      if (isEditMode && !hasLoadedInitialData) {
        try {
          // Always fetch tasks first to ensure we have the latest data
          await fetchTasksByIteration(projectId, iterationId);

          // Give state a moment to update
          await new Promise(resolve => setTimeout(resolve, 200));

          // Now check if we have the task
          const currentTasks = tasksByIteration[iterationId] || [];
          const task = currentTasks.find((t) => t.id === taskId);

          if (task) {
            // Handle assigned users from the current assignee object/details
            let assignedUsers = [];
            const taskAssignee = getTaskAssigneeInfo(task);

            if (taskAssignee.assigneeId) {
              let fallbackName =
                taskAssignee.normalizedName ||
                (taskAssignee.label !== "Unknown" ? taskAssignee.label : "") ||
                "";

              if (!fallbackName) {
                try {
                  const assigneeProfile = await getUserById(taskAssignee.assigneeId);
                  fallbackName =
                    `${assigneeProfile?.firstName || ""} ${assigneeProfile?.lastName || ""}`.trim() ||
                    assigneeProfile?.fullName ||
                    assigneeProfile?.email ||
                    assigneeProfile?.username ||
                    "";
                } catch (error) {
                  console.warn("Failed to resolve assignee profile by id:", error);
                }
              }

              assignedUsers = [{
                value: taskAssignee.assigneeId,
                label: fallbackName || "Unknown",
              }];
            } else if (taskAssignee.label && taskAssignee.label !== "Unknown") {
              try {
                // Search for the user by name to get their full user object
                const userResults = await searchUserProfiles(taskAssignee.label);
                const matchingUser = userResults.find(user =>
                  `${user.firstName} ${user.lastName}` === taskAssignee.label ||
                  user.email === taskAssignee.label
                );

                if (matchingUser) {
                  const matchingUserFullName =
                    `${matchingUser.firstName || ""} ${matchingUser.lastName || ""}`.trim();
                  assignedUsers = [{
                    value: matchingUser.id,
                    label: matchingUserFullName || taskAssignee.label,
                  }];
                } else {
                  // If user not found, create a placeholder (this shouldn't happen in normal cases)
                  console.warn(`User "${taskAssignee.label}" not found in user database`);
                  assignedUsers = [{
                    value: taskAssignee.label,
                    label: taskAssignee.label,
                  }];
                }
              } catch (error) {
                console.error("Error searching for assignee:", error);
                // Fallback: create placeholder
                assignedUsers = [{
                  value: taskAssignee.label,
                  label: taskAssignee.label,
                }];
              }
            }

            const newFormData = {
              title: task.title || "",
              description: task.description || "",
              acceptanceCriteria: task.acceptanceCriteria || "",
              status: task.status || "OPEN",
              priority: task.priority !== undefined ? task.priority : 2,
              points: task.points !== undefined ? task.points : 1,
              assignedUsers: assignedUsers,
              startedAt: task.startedAt ? task.startedAt.split('T')[0] : "",
              expectedCompletionDate: task.expectedCompletionDate ? task.expectedCompletionDate.split('T')[0] : "",
              links: Array.isArray(task.links) ? task.links : [],
              textFiles: dedupeTextFilesByFileId(
                Array.isArray(task.textFiles) ? task.textFiles : [],
              ),
            };
            initialTextFileIdsRef.current = new Set(
              newFormData.textFiles.map((file) => file.fileId),
            );

            setAttachments(
              Array.isArray(task.attachments)
                ? task.attachments.map((a) => ({
                    fileId: a.fileId,
                    fileName: a.fileName || a.label || "attachment",
                    label: a.label || null,
                  }))
                : []
            );

            setFormData(newFormData);
            setHasLoadedInitialData(true);
            setIsLoading(false);
          } 
          } catch (error) {
          console.error("Failed to load task data:", error);
          toast.error("Failed to load task data");
            setIsLoading(false);
        }
      } else if (!isEditMode) {
        initialTextFileIdsRef.current = new Set();
        setIsLoading(false);
      }
    };

    loadTaskData();
  }, [
    taskId,
    isEditMode,
    hasLoadedInitialData,
    fetchTasksByIteration,
    projectId,
    iterationId,
    tasksByIteration,
    getUserById,
    searchUserProfiles,
  ]);

  // Search users when debounced search term changes (like users table)
  useEffect(() => {
    const searchUsers = async () => {
      if (debouncedUserSearch.trim()) {
        setIsSearchingUsers(true);
        try {
          const results = await searchUserProfiles(debouncedUserSearch.trim());
          setSearchedUsers(results);
        } catch (error) {
          console.error("Error searching users:", error);
          setSearchedUsers([]);
        } finally {
          setIsSearchingUsers(false);
        }
      } else {
        setSearchedUsers([]);
      }
    };

    searchUsers();
  }, [debouncedUserSearch, searchUserProfiles]);

  // Add user to assigned users
  const addUserToTask = (user) => {
    const isAlreadyAssigned = formData.assignedUsers.some(u => u.value === user.id);
    if (!isAlreadyAssigned) {
      const fullName = `${user.firstName || ""} ${user.lastName || ""}`.trim();
      setFormData(prev => ({
        ...prev,
        assignedUsers: [...prev.assignedUsers, {
          value: user.id,
          label: fullName || user.fullName || user.username || user.email || "Unknown",
        }]
      }));
      setUserSearchTerm(""); // Clear search after adding
      setSearchedUsers([]); // Clear results
    }
  };

  // Remove user from assigned users
  const removeUserFromTask = (userId) => {
    setFormData(prev => ({
      ...prev,
      assignedUsers: prev.assignedUsers.filter(u => u.value !== userId)
    }));
  };

  const handleChange = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const getImageDimensions = (file) =>
    new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve({ width: img.width, height: img.height });
      img.onerror = reject;
      img.src = URL.createObjectURL(file);
    });

  const applyInlineImageReplacements = (html = "") => {
    if (!html) return html;

    // Ensure all uploads finished
    if (inlineUploads.current.inFlight > 0) {
      throw new Error("inline-uploads-in-progress");
    }

    const regex = /!\[Uploading[^\]]*\]\(upload:\/\/([^)]+)\)/g;
    let resultHtml = html;
    let match;
    while ((match = regex.exec(html)) !== null) {
      const markerId = match[1];
      const replacement = inlineUploads.current.pending.get(markerId);
      if (!replacement) {
        throw new Error("inline-upload-missing");
      }

      const { fileId, width, height, alt } = replacement;
      const imgTag = `<img src="file-service://${fileId}" alt="${alt}" width="${width}" height="${height}" />`;
      resultHtml = resultHtml.replace(match[0], imgTag);
      inlineUploads.current.pending.delete(markerId);
    }

    return resultHtml;
  };

  const handleImagePaste = useCallback(
    async (file, helpers) => {
      const log = (step, extra = {}) =>
        console.info("[TaskForm:InlineImage]", step, {
          name: file?.name,
          type: file?.type,
          size: file?.size,
          ...extra,
        });

      const { insertPlaceholder, replacePlaceholderWithText } = helpers;

      // GitHub-like UX: show a temporary "Uploading..." marker as plain text.
      const extension = file.type?.split("/")[1] || "png";
      const filename =
        file.name && file.name.trim().length > 0
          ? file.name
          : `pasted-${Date.now()}.${extension}`;
      const markerId = `upload-${Date.now()}-${Math.random()
        .toString(36)
        .slice(2, 8)}`;
      const placeholderText = `![Uploading ${filename}…](upload://${markerId})`;

      // Insert placeholder immediately so the user sees progress
      const placeholderRange = insertPlaceholder(placeholderText);
      inlineUploads.current.inFlight += 1;

      try {
        log("start");
        const { width, height } = await getImageDimensions(file);
        log("dimensions", { width, height });

        const fileForUpload = new File([file], filename, {
          type: file.type || "image/png",
        });

        log("uploading", { filename });
        const { fileId } = await uploadFile(user?.id, fileForUpload, {
          purpose: "TASK_PROFILE",
          fileType: "PHOTO",
        });
        log("uploaded", { fileId });

        // Stash replacement info; actual replacement to <img ...> happens on submit.
        inlineUploads.current.pending.set(markerId, {
          fileId,
          width,
          height,
          alt: filename.replace(/\.[^/.]+$/, ""),
        });
        log("store-pending-replacement", { markerId, fileId });

        setFormData((prev) => {
          const existing = prev.textFiles || [];
          if (existing.some((tf) => tf.fileId === fileId)) {
            log("duplicate-fileId-skip-textFiles");
            return prev;
          }
          log("append-textFile", { fileId });
          return {
            ...prev,
            textFiles: [...existing, { fileId, width, height }],
          };
        });
      } catch (err) {
        console.error("Inline image upload failed", err);
        replacePlaceholderWithText(placeholderRange, "(image upload failed)");
        toast.error("Failed to upload pasted image. Please try again.");
      } finally {
        inlineUploads.current.inFlight = Math.max(
          0,
          inlineUploads.current.inFlight - 1
        );
      }
    },
    [uploadFile, user?.id]
  );
  useEffect(() => {
    if (typeof window !== "undefined") {
      window.__taskFormImagePaste = handleImagePaste;
      window.__globalImagePasteHandler = handleImagePaste;
    }
    return () => {
      if (typeof window !== "undefined") {
        if (window.__taskFormImagePaste === handleImagePaste) {
          delete window.__taskFormImagePaste;
        }
        if (window.__globalImagePasteHandler === handleImagePaste) {
          delete window.__globalImagePasteHandler;
        }
      }
    };
  }, [handleImagePaste]);
  console.info("[TaskFormPage] render handleImagePaste", !!handleImagePaste);

  console.info("[TaskFormPage] render", { hasHandleImagePaste: !!handleImagePaste });

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      toast.error("Task title is required");
      return;
    }

    // Strip and block raw data URIs to prevent oversized DB payloads
    const stripDataImages = (html = "") =>
      html
        // Remove <img src="data:image...">
        .replace(/<img[^>]*src="data:image[^"]*"[^>]*>/gi, "")
        // Remove any remaining data:image URLs
        .replace(/data:image\/[a-zA-Z0-9+.-]+;base64,[^"'>\s)]+/gi, "");

    const cleanedDescription = stripDataImages(formData.description || "");
    const cleanedAcceptance = stripDataImages(formData.acceptanceCriteria || "");

    if ((formData.description || "") !== cleanedDescription) {
      toast.error("Inline base64 images were removed. Please re-add images via paste/upload.");
      return;
    }

    let finalDescription;
    try {
      finalDescription = applyInlineImageReplacements(cleanedDescription);
    } catch (err) {
      if (err.message === "inline-uploads-in-progress") {
        toast.error("Please wait for inline image uploads to finish.");
        return;
      }
      if (err.message === "inline-upload-missing") {
        toast.error("An inline image is still processing. Please retry in a moment.");
        return;
      }
      throw err;
    }

    const maxLen = 7800; // safety buffer below DB column limit (8000)
    if ((finalDescription || "").length > maxLen || cleanedAcceptance.length > maxLen) {
      toast.error("Description or acceptance criteria is too long. Please shorten or split content.");
      return;
    }

    if (!iterationId) {
      toast.error("Iteration ID is required");
      return;
    }

    if (!projectId) {
      toast.error("Project ID is required");
      return;
    }

    setIsSubmitting(true);

    try {
      const normalizedTextFiles = dedupeTextFilesByFileId(formData.textFiles || []);
      const referencedFileIds = extractReferencedFileIds(
        finalDescription || "",
        cleanedAcceptance || "",
      );
      const referencedTextFiles = referencedFileIds.size
        ? normalizedTextFiles.filter((file) => referencedFileIds.has(file.fileId))
        : normalizedTextFiles;
      const textFilesForPayload = isEditMode
        ? referencedTextFiles.filter(
            (file) => !initialTextFileIdsRef.current.has(file.fileId),
          )
        : referencedTextFiles;

      const taskData = {
        type: formData.type || "TASK",
        title: formData.title.trim(),
        description: finalDescription || null,
        acceptanceCriteria: cleanedAcceptance || null,
        status: formData.status,
        priority: formData.priority,
        points: formData.points,
        iterationId: iterationId,
        startedAt: formData.startedAt || null,
        expectedCompletionDate: formData.expectedCompletionDate || null,
        actualCompletionDate: null,
        comment: "",
        links: formData.links || [],
        textFiles: textFilesForPayload,
        // API expects assigneeId as number, not assignedUserIds array
        ...(formData.assignedUsers && formData.assignedUsers.length > 0 && {
          assigneeId: formData.assignedUsers[0].value,
        }),
      };

      if (isEditMode) {
        await updateTask(projectId, taskId, taskData);
        toast.success("Task updated successfully");
        // Dispatch event to refresh tasks in other components
        window.dispatchEvent(new CustomEvent("taskUpdated", {
          detail: { projectId, iterationId, taskId }
        }));
      } else {
        await createTask(projectId, taskData);
        console.log("task Data")
        toast.success("Task created successfully");
      }

      navigate(paths.projectIterationDetails(projectId, iterationId));
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || error.message || 
        `Failed to ${isEditMode ? "update" : "create"} task`;
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-3 mt-3 gap-3">
        <WidgetSectionTitle
          icon="tasks"
          title={isEditMode ? "Edit Task" : "Create New Task"}
          subtitle={isEditMode ? "Update task details" : "Create a new task for the selected iteration"}
          className="mb-3 mt-3 flex-1"
        />

        <Button
          variant="info"
          size="sm"
          className="text-sm"
          onClick={() => navigate(-1)}
        >
          Go Back
        </Button>
      </div>

      <Card className="custom-shadow">
        <Card.Body>
          <Form onSubmit={handleSubmit}>
            {/* Show loading state for edit mode */}
            {isLoading && isEditMode && (
              <div className="text-center py-4">
                <Spinner animation="border" variant="primary" />
                <div className="mt-2 text-muted">Loading task data...</div>
              </div>
            )}

            {/* Show form only when not loading or not in edit mode */}
            {(!isLoading || !isEditMode) && (
              <>
            <Form.Group className="mb-4">
              <Form.Label>Task Title</Form.Label>
              <Form.Control
                type="text"
                value={formData.title}
                onChange={(e) => handleChange("title", e.target.value)}
                placeholder="Enter task title"
                required
                disabled={isSubmitting}
                autoComplete="off"
              />
            </Form.Group>

            <MentionEditor
              label="Description"
              value={formData.description}
              onChange={(html) => handleChange("description", html)}
              users={mentionUsers}
              onImagePaste={handleImagePaste}
              debugId="TaskFormPage:Description"
              className="mb-4"
            />

            <Form.Group className="mb-4 mt-6">
              <Form.Label>Acceptance Criteria</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={formData.acceptanceCriteria}
                onChange={(e) => handleChange("acceptanceCriteria", e.target.value)}
                placeholder="Define the acceptance criteria for this task"
                disabled={isSubmitting}
              />
            </Form.Group>

            <Row className="mt-4">
              <Col md={6} className="mb-3">
                <Form.Label>Priority</Form.Label>
                <Form.Select
                  value={formData.priority}
                  onChange={(e) =>
                    handleChange("priority", parseInt(e.target.value))
                  }
                  required
                  disabled={isSubmitting}
                >
                  {priorityOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Select>
              </Col>
            </Row>

            <Row className="mt-3">
              <Col md={4} className="mb-3">
                <Form.Label>Points (Days)</Form.Label>
                <Form.Select
                  value={formData.points}
                  onChange={(e) =>
                    handleChange("points", parseInt(e.target.value))
                  }
                  required
                  disabled={isSubmitting}
                >
                  {pointsOptions.map((point) => (
                    <option key={point} value={point}>
                      {point} {point === 1 ? "day" : "days"}
                    </option>
                  ))}
                </Form.Select>
                <Form.Text className="text-muted d-block">
                  Each point represents 1 day of work
                </Form.Text>
              </Col>

              <Col md={4} className="mb-3">
                <Form.Label>Started At</Form.Label>
                <Form.Control
                  type="date"
                  value={formData.startedAt}
                  onChange={(e) => handleChange("startedAt", e.target.value)}
                  disabled={isSubmitting}
                />
              </Col>

              <Col md={4} className="mb-3">
                <Form.Label>Expected Completion Date</Form.Label>
                <Form.Control
                  type="date"
                  value={formData.expectedCompletionDate}
                  onChange={(e) => handleChange("expectedCompletionDate", e.target.value)}
                  disabled={isSubmitting}
                />
              </Col>
            </Row>

            <Form.Group className="mb-4 mt-3">
              <Form.Label>Assign Users</Form.Label>

              {/* Search Input - Like users table */}
              <InputGroup className="mb-2">
                <InputGroup.Text>
                  <FontAwesomeIcon icon="search" />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Search users..."
                  value={userSearchTerm}
                  onChange={(e) => setUserSearchTerm(e.target.value)}
                  disabled={isSubmitting}
                />
                {isSearchingUsers && (
                  <InputGroup.Text>
                    <Spinner animation="border" size="sm" />
                  </InputGroup.Text>
                )}
              </InputGroup>

              {/* Assigned Users Display */}
              {formData.assignedUsers.length > 0 && (
                <div className="mb-3">
                  <small className="text-muted d-block mb-2">Assigned Users:</small>
                  <div className="d-flex flex-wrap gap-2">
                    {formData.assignedUsers.map((user) => (
                      <span
                        key={user.value}
                        className="badge bg-primary d-flex align-items-center"
                        style={{ fontSize: "0.75rem" }}
                      >
                        {user.label}
                        <button
                          type="button"
                          className="btn btn-sm p-0 ms-2 text-white border-0 bg-transparent"
                          onClick={() => removeUserFromTask(user.value)}
                          disabled={isSubmitting}
                          style={{ fontSize: "0.6rem", lineHeight: 1 }}
                        >
                          <FontAwesomeIcon icon="times" />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Search Results - Like users table results */}
              {userSearchTerm && (
                <div className="border rounded p-2" style={{ maxHeight: "200px", overflowY: "auto" }}>
                  {isSearchingUsers ? (
                    <div className="text-center py-3">
                      <Spinner animation="border" size="sm" />
                      <small className="text-muted d-block mt-1">Searching...</small>
                    </div>
                  ) : searchedUsers.length > 0 ? (
                    searchedUsers.slice(0, 10).map((user) => {
                      const isAlreadyAssigned = formData.assignedUsers.some(u => u.value === user.id);
                      const resultFullName =
                        `${user.firstName || ""} ${user.lastName || ""}`.trim() ||
                        user.fullName ||
                        user.username ||
                        "User";
                      return (
                        <div
                          key={user.id}
                          className={`d-flex align-items-center justify-content-between p-2 rounded ${
                            isAlreadyAssigned ? "bg-light" : "hover-bg-light"
                          }`}
                          style={{ cursor: isAlreadyAssigned ? "default" : "pointer" }}
                          onClick={() => !isAlreadyAssigned && addUserToTask(user)}
                        >
                          <div>
                            <div className="fw-medium" style={{ fontSize: "0.875rem" }}>
                              {resultFullName}
                            </div>
                          </div>
                          {isAlreadyAssigned ? (
                            <small className="text-success fw-medium">Assigned</small>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline-primary"
                              disabled={isSubmitting}
                              style={{ fontSize: "0.75rem", padding: "2px 8px" }}
                            >
                              Add
                            </Button>
                          )}
                        </div>
                      );
                    })
                  ) : userSearchTerm.length >= 2 ? (
                    <div className="text-center py-3 text-muted">
                      <small>No users found</small>
                    </div>
                  ) : (
                    <div className="text-center py-3 text-muted">
                      <small>Type at least 2 characters to search</small>
                    </div>
                  )}
                </div>
              )}

              <Form.Text className="text-muted d-block">
                Search and assign users to this task
              </Form.Text>
            </Form.Group>

            <div className="d-flex justify-content-end mt-6">
              <Button
                variant="success"
                type="submit"
                size="sm"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    {isEditMode ? "Updating..." : "Creating..."}
                  </>
                ) : (
                  isEditMode ? "Update Task" : "Create Task"
                )}
              </Button>
            </div>
              </>
            )}
          </Form>
        </Card.Body>
      </Card>
    </>
  );
};

export default TaskFormPage;
